import {
  convertDecimalStringsToNumbers,
  convertDecimalsToPercentage,
  convertPercentagesToDecimal,
  transformToCamelCase,
  transformToSnakeCase,
} from '@/lib/transformations';
import { apiClient } from '@/services/api';
import type {
  CalculatedTotals,
  LineItem,
  LoanTerms,
  Party,
  Transaction,
  ValidationResult,
} from '../types';

/**
 * Poll document status with exponential backoff
 * @param documentId - Document ID to poll
 * @param maxAttempts - Maximum number of poll attempts (default: 30)
 * @param initialDelay - Initial delay in milliseconds (default: 1000ms)
 * @param maxDelay - Maximum delay between polls (default: 5000ms)
 * @returns Document status response
 */
export async function pollDocumentStatusWithBackoff(
  documentId: string,
  maxAttempts = 30,
  initialDelay = 1000,
  maxDelay = 5000,
): Promise<{ success: boolean; data: any }> {
  let attempt = 0;
  let delay = initialDelay;

  while (attempt < maxAttempts) {
    attempt++;

    try {
      const response = await apiClient.get<{
        success: boolean;
        data: {
          documentId: string;
          transactionId: string;
          fileName: string;
          documentType: string;
          processingStatus: 'pending' | 'processing' | 'completed' | 'failed';
          uploadedAt: string;
          updatedAt: string;
          extractionData?: unknown;
          errorMessage?: string;
        };
      }>(`/documents/${documentId}/status`);

      // Check if processing is complete (success or failure)
      if (
        response.data.processingStatus === 'completed' ||
        response.data.processingStatus === 'failed'
      ) {
        return response;
      }

      // If still processing, wait before next poll with exponential backoff
      if (attempt < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, delay));
        // Exponential backoff: double the delay each time, capped at maxDelay
        delay = Math.min(delay * 2, maxDelay);
      }
    } catch (error) {
      // On error, wait and retry (unless it's the last attempt)
      if (attempt < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, delay));
        delay = Math.min(delay * 2, maxDelay);
      } else {
        throw error;
      }
    }
  }

  throw new Error('Document processing timeout. Please try again or contact support.');
}

/**
 * Closing Disclosure API service
 *
 * Handles all API calls related to closing disclosure transactions
 */
export const closingDisclosureApi = {
  // Transaction CRUD
  async create(transaction: Omit<Transaction, 'id' | 'created_at' | 'updated_at'>) {
    return apiClient.post<{ success: boolean; data: { transactionId: string } }>(
      '/transactions',
      transaction,
    );
  },

  async getById(id: string) {
    return apiClient.get<{ success: boolean; data: Transaction }>(`/transactions/${id}`);
  },

  async update(id: string, transaction: Partial<Transaction>) {
    return apiClient.put<{ success: boolean; data: Transaction }>(
      `/transactions/${id}`,
      transaction,
    );
  },

  async list(params?: { status?: string; limit?: number; offset?: number }) {
    const query = new URLSearchParams();
    if (params?.status) query.append('status', params.status);
    if (params?.limit !== undefined && params?.limit !== null)
      query.append('limit', String(params.limit));
    if (params?.offset !== undefined && params?.offset !== null)
      query.append('offset', String(params.offset));

    return apiClient.get<{
      success: boolean;
      data: { transactions: Transaction[]; total: number };
    }>(`/transactions?${query}`);
  },

  async delete(id: string) {
    return apiClient.delete<{ success: boolean }>(`/transactions/${id}`);
  },

  // Loan Terms
  async saveLoanTerms(transactionId: string, loanTerms: Omit<LoanTerms, 'id' | 'transaction_id'>) {
    // Fields that are percentages in frontend but decimals in backend
    const percentageFields = [
      'interest_rate',
      'max_rate_increase',
      'max_rate_decrease',
      'rate_adjustment_cap',
    ];

    // Convert percentage values to decimal format
    const convertedData = convertPercentagesToDecimal(
      loanTerms as unknown as Record<string, unknown>,
      percentageFields,
    );

    return apiClient.post<{ success: boolean; data: LoanTerms }>(
      `/transactions/${transactionId}/loan-terms`,
      convertedData,
    );
  },

  async getLoanTerms(transactionId: string) {
    const response = await apiClient.get<{ success: boolean; data: Record<string, unknown> }>(
      `/transactions/${transactionId}/loan-terms`,
    );

    if (response.success && response.data) {
      // Sequelize returns DECIMAL fields as strings - convert to numbers
      const numericFields = [
        'loan_amount',
        'monthly_principal_and_interest',
        'balloon_payment_amount',
        'estimated_escrow_payment',
        'estimated_total_monthly_payment',
        'estimated_taxes_insurance_assessments',
        'total_of_payments',
        'finance_charge',
        'prepayment_penalty_max_amount',
      ];
      let convertedData = convertDecimalStringsToNumbers(response.data, numericFields);

      // Convert decimal values to percentage format (keys are already snake_case)
      const percentageFields = [
        'interest_rate',
        'max_rate_increase',
        'max_rate_decrease',
        'rate_adjustment_cap',
        'annual_percentage_rate',
        'total_interest_percentage',
      ];
      convertedData = convertDecimalsToPercentage(convertedData, percentageFields);

      return {
        success: response.success,
        data: convertedData as unknown as LoanTerms,
      };
    }

    return response as unknown as { success: boolean; data: LoanTerms };
  },

  async deleteLoanTerms(transactionId: string) {
    return apiClient.delete<{ success: boolean }>(`/transactions/${transactionId}/loan-terms`);
  },

  // Parties
  async saveParties(transactionId: string, parties: Omit<Party, 'id'>[]) {
    return apiClient.post<{ success: boolean; data: Party[] }>(
      `/transactions/${transactionId}/parties`,
      { parties },
    );
  },

  async getParties(transactionId: string) {
    return apiClient.get<{ success: boolean; data: Party[] }>(
      `/transactions/${transactionId}/parties`,
    );
  },

  async updateParty(transactionId: string, partyId: string, party: Partial<Party>) {
    return apiClient.put<{ success: boolean; data: Party }>(
      `/transactions/${transactionId}/parties/${partyId}`,
      party,
    );
  },

  async deleteParty(transactionId: string, partyId: string) {
    return apiClient.delete<{ success: boolean }>(
      `/transactions/${transactionId}/parties/${partyId}`,
    );
  },

  // Line Items
  async getLineItems(transactionId: string) {
    const response = await apiClient.get<{ success: boolean; data: Record<string, unknown>[] }>(
      `/transactions/${transactionId}/line-items`,
    );

    if (response.success && response.data) {
      // Sequelize returns DECIMAL fields as strings - convert to numbers
      // Fields are in snake_case at this point
      const numericFields = [
        'borrower_paid_at_closing',
        'borrower_paid_before_closing',
        'seller_paid_at_closing',
        'seller_paid_before_closing',
        'paid_by_others',
      ];

      const transformedData = response.data.map((item) => {
        // First convert DECIMAL strings to numbers
        const withNumbers = convertDecimalStringsToNumbers(item, numericFields);
        // Then transform snake_case to camelCase
        return transformToCamelCase(withNumbers) as unknown as LineItem;
      });

      return {
        success: response.success,
        data: transformedData,
      };
    }

    return response as unknown as { success: boolean; data: LineItem[] };
  },

  async addLineItemsSection(
    transactionId: string,
    section: string,
    useTemplate = false,
    items?: Omit<LineItem, 'id' | 'transactionId'>[],
  ) {
    const transformedItems = items?.map((item) =>
      transformToSnakeCase(item as unknown as Record<string, unknown>),
    );
    const response = await apiClient.post<{ success: boolean; data: Record<string, unknown>[] }>(
      `/transactions/${transactionId}/line-items/section/${section}`,
      { use_template: useTemplate, items: transformedItems },
    );

    if (response.success && response.data) {
      // Backend returns snake_case with DECIMAL strings - transform to match frontend format
      const numericFields = [
        'borrower_paid_at_closing',
        'borrower_paid_before_closing',
        'seller_paid_at_closing',
        'seller_paid_before_closing',
        'paid_by_others',
      ];

      const transformedData = response.data.map((item) => {
        // First convert DECIMAL strings to numbers
        const withNumbers = convertDecimalStringsToNumbers(item, numericFields);
        // Then transform snake_case to camelCase
        return transformToCamelCase(withNumbers) as unknown as LineItem;
      });

      return {
        success: response.success,
        data: transformedData,
      };
    }

    return response as unknown as { success: boolean; data: LineItem[] };
  },

  async saveLineItems(transactionId: string, items: (LineItem & { id?: string })[]) {
    const transformedItems = items.map((item) =>
      transformToSnakeCase(item as unknown as Record<string, unknown>),
    );
    const response = await apiClient.post<{ success: boolean; data: Record<string, unknown>[] }>(
      `/transactions/${transactionId}/line-items`,
      { items: transformedItems },
    );

    if (response.success && response.data) {
      // Backend returns snake_case with DECIMAL strings - transform to match frontend format
      const numericFields = [
        'borrower_paid_at_closing',
        'borrower_paid_before_closing',
        'seller_paid_at_closing',
        'seller_paid_before_closing',
        'paid_by_others',
      ];

      const transformedData = response.data.map((item) => {
        // First convert DECIMAL strings to numbers
        const withNumbers = convertDecimalStringsToNumbers(item, numericFields);
        // Then transform snake_case to camelCase
        return transformToCamelCase(withNumbers) as unknown as LineItem;
      });

      return {
        success: response.success,
        data: transformedData,
      };
    }

    return response as unknown as { success: boolean; data: LineItem[] };
  },

  async updateLineItem(transactionId: string, itemId: string, item: Partial<LineItem>) {
    const transformedItem = transformToSnakeCase(item as unknown as Record<string, unknown>);
    return apiClient.put<{ success: boolean; data: LineItem }>(
      `/transactions/${transactionId}/line-items/${itemId}`,
      transformedItem,
    );
  },

  async deleteLineItem(transactionId: string, itemId: string) {
    return apiClient.delete<{ success: boolean }>(
      `/transactions/${transactionId}/line-items/${itemId}`,
    );
  },

  async getSectionTemplate(section: string) {
    return apiClient.get<{ success: boolean; data: { section: string; items: unknown[] } }>(
      `/transactions/line-items/section/${section}/template`,
    );
  },

  // Calculations
  async validateTransaction(transactionId: string) {
    return apiClient.get<{ success: boolean; data: ValidationResult }>(
      `/transactions/${transactionId}/validate`,
    );
  },

  async getCalculations(transactionId: string) {
    return apiClient.get<{ success: boolean; data: CalculatedTotals }>(
      `/transactions/${transactionId}/calculations`,
    );
  },

  // PDF Generation
  async generatePDF(
    transactionId: string,
    version: 'draft' | 'final' = 'draft',
    includeWatermark = true,
  ) {
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:9011/api';
    const response = await fetch(`${API_BASE_URL}/transactions/${transactionId}/generate-cd`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ version, includeWatermark }),
    });

    if (!response.ok) {
      throw new Error('Failed to generate PDF');
    }

    // Get the filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `closing-disclosure-${transactionId}-${version}.pdf`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch?.[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    // Get blob and create download link
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    return { success: true, filename };
  },

  async downloadPDF(transactionId: string, documentId: string) {
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:9011/api';
    const response = await fetch(
      `${API_BASE_URL}/transactions/${transactionId}/download/${documentId}`,
      {
        credentials: 'include',
      },
    );
    if (!response.ok) throw new Error('Failed to download PDF');
    return response.blob();
  },

  // Document Operations
  async getDocumentStatus(documentId: string) {
    return apiClient.get<{
      success: boolean;
      data: {
        documentId: string;
        transactionId: string;
        fileName: string;
        documentType: string;
        processingStatus: 'pending' | 'processing' | 'completed' | 'failed';
        uploadedAt: string;
        updatedAt: string;
        extractionData?: unknown;
        errorMessage?: string;
      };
    }>(`/documents/${documentId}/status`);
  },

  async uploadDocumentStandalone(
    file: File,
    documentType = 'cd',
    orderId?: string,
    autoProcess = false,
  ) {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('document_type', documentType);
    if (orderId) {
      formData.append('order_id', orderId);
    }
    formData.append('auto_process', autoProcess ? 'true' : 'false');

    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:9011/api';
    const response = await fetch(`${API_BASE_URL}/documents/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to upload document');
    }

    return response.json();
  },

  async getDocuments(transactionId: string) {
    return apiClient.get<{
      success: boolean;
      data: {
        transactionId: string;
        documents: Array<{
          documentId: string;
          fileName: string;
          documentType: string;
          processingStatus: string;
          uploadedAt: string;
          fileSize: number;
          validationSessionId?: string;
        }>;
      };
    }>(`/transactions/${transactionId}/documents`);
  },

  async getSourceDocuments(transactionId: string) {
    return apiClient.get<{
      success: boolean;
      data: {
        transactionId: string;
        documents: Array<{
          documentId: string;
          fileName: string;
          documentType: string;
          processingStatus: string;
          uploadedAt: string;
          fileSize: number;
        }>;
      };
    }>(`/transactions/${transactionId}/documents/source`);
  },

  async getDocument(transactionId: string, documentId: string) {
    return apiClient.get<{
      success: boolean;
      data: {
        documentId: string;
        transactionId: string;
        fileName: string;
        documentType: string;
        processingStatus: string;
        fileSize: number;
        mimeType: string;
        uploadedAt: string;
        updatedAt: string;
        extractionData?: unknown;
        errorMessage?: string;
      };
    }>(`/transactions/${transactionId}/documents/${documentId}`);
  },

  async uploadDocument(
    transactionId: string,
    file: File,
    documentType = 'cd',
    autoProcess = false,
  ) {
    const formData = new FormData();
    // Must use 'document' as the field name - matching multer field name in backend
    formData.append('document', file);
    formData.append('document_type', documentType);
    formData.append('auto_process', autoProcess ? 'true' : 'false');

    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:9011/api';
    const response = await fetch(`${API_BASE_URL}/transactions/${transactionId}/documents/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to upload document');
    }

    return response.json();
  },

  async downloadDocument(transactionId: string, documentId: string) {
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:9011/api';
    const response = await fetch(
      `${API_BASE_URL}/transactions/${transactionId}/documents/${documentId}/download`,
      {
        credentials: 'include',
      },
    );
    if (!response.ok) throw new Error('Failed to download document');
    return response.blob();
  },

  async deleteDocument(transactionId: string, documentId: string) {
    return apiClient.delete<{ success: boolean }>(
      `/transactions/${transactionId}/documents/${documentId}`,
    );
  },

  async retryDocument(documentId: string) {
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:9011/api';
    const response = await fetch(`${API_BASE_URL}/documents/${documentId}/retry`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Failed to retry document processing');
    }
    return response.json();
  },

  async processDocument(documentId: string) {
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:9011/api';
    const response = await fetch(`${API_BASE_URL}/documents/${documentId}/process`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to process document');
    }
    return response.json();
  },

  // Validation Session Operations
  async getValidationSession(sessionId: string) {
    return apiClient.get<{
      success: boolean;
      data: {
        sessionId: string;
        documentId: string;
        transactionId: string;
        extractedData: Record<string, unknown>;
        selectedFields?: Record<string, unknown>;
        status: string;
        expiresAt: string;
        documentUrl?: string;
      };
      error?: string;
    }>(`/validation/sessions/${sessionId}`);
  },

  async updateValidationSession(sessionId: string, selectedFields: Record<string, unknown>) {
    return apiClient.patch<{
      success: boolean;
      data: {
        sessionId: string;
        selectedFields: Record<string, unknown>;
      };
      error?: string;
    }>(`/validation/sessions/${sessionId}`, { selectedFields });
  },

  async submitValidationSession(sessionId: string, selectedFields?: Record<string, unknown>) {
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:9011/api';
    const response = await fetch(`${API_BASE_URL}/validation/sessions/${sessionId}/submit`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(selectedFields ? { selectedFields } : {}),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to submit validation session');
    }
    const data = await response.json();
    return data as { success: boolean; message?: string; redirectUrl?: string; error?: string };
  },

  async cancelValidationSession(sessionId: string) {
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:9011/api';
    const response = await fetch(`${API_BASE_URL}/validation/sessions/${sessionId}/cancel`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to cancel validation session');
    }
    const data = await response.json();
    return data as { success: boolean; message?: string; error?: string };
  },

  // Version Operations
  async getVersions(transactionId: string) {
    return apiClient.get<{
      success: boolean;
      data: {
        transactionId: string;
        versions: Array<{
          id: string;
          version_number: number;
          version_type: string;
          snapshot_data?: {
            draft: {
              status: string;
              form_data: Record<string, unknown>;
              calculated_totals: Record<string, unknown>;
              validation_errors: Record<string, unknown>;
            };
            parties: Array<Record<string, unknown>>;
            lineItems: Array<Record<string, unknown>>;
            loanTerms: Record<string, unknown> | null;
          };
          created_at: string;
          created_by_user_id: string;
          change_summary: string;
          createdBy?: {
            id: string;
            name: string;
            email: string;
          };
        }>;
      };
    }>(`/transactions/${transactionId}/versions`);
  },

  async getLatestVersion(transactionId: string) {
    return apiClient.get<{
      success: boolean;
      data: {
        id: string;
        version_number: number;
        version_type: string;
        snapshot_data?: {
          draft: {
            status: string;
            form_data: Record<string, unknown>;
            calculated_totals: Record<string, unknown>;
            validation_errors: Record<string, unknown>;
          };
          parties: Array<Record<string, unknown>>;
          lineItems: Array<Record<string, unknown>>;
          loanTerms: Record<string, unknown> | null;
        };
        created_at: string;
        created_by_user_id: string;
        createdBy?: {
          id: string;
          name: string;
          email: string;
        };
      } | null;
    }>(`/transactions/${transactionId}/versions/latest`);
  },

  async getVersionDiff(transactionId: string, versionId: string) {
    return apiClient.get<{
      success: boolean;
      data: {
        versionId: string;
        changes: Array<{
          field: string;
          oldValue: unknown;
          newValue: unknown;
        }>;
      };
    }>(`/transactions/${transactionId}/versions/${versionId}/diff`);
  },

  async createVersion(transactionId: string, data: { versionType: string; notes?: string }) {
    return apiClient.post<{
      success: boolean;
      data: {
        id: string;
        version_number: number;
        version_type: string;
        created_at: string;
      };
    }>(`/transactions/${transactionId}/versions`, data);
  },

  async restoreVersion(transactionId: string, versionId: string) {
    return apiClient.post<{
      success: boolean;
      data: {
        id: string;
        version_number: number;
        restored_from: string;
      };
    }>(`/transactions/${transactionId}/versions/${versionId}/restore`, {});
  },
};

export default closingDisclosureApi;
